// Product listings (02 Collection, 08 Search) build on links and a GET form, so everything works without JavaScript. This script adds:
// - live filtering: changing a filter or the sort re-renders the section through the Section Rendering API and updates the URL;
//   the price slider fills the To field as it moves;
// - Load more, which appends the next page to the grid (and, on search, to the articles and help list);
// - the filter sheet (below 1100 px on the collection, always on search), opened from the filter bar, at a filter's group
//   when one of its pills opened it, and the sort sheet below 750 px (on search from 750 px a popover under its button);
// - the grid density switch on a wide collection page;
// - the per-type counts on the search tabs that the page could not count itself (plus the Help answers it shows), and the
//   Setups tab's aria-current while its panel is the target;
// - the "Did you mean" suggestions on a search with no results;
// - the phone search bar's Back, which goes back in history when the shopper came from this store.

// The query string the filter form submits: empty price fields are dropped, so a cleared field does not filter on 0.
export function facetQuery(entries) {
  const params = new URLSearchParams();
  for (const [name, value] of entries) {
    if (/^filter\.v\.price\.(gte|lte)$/.test(name) && String(value).trim() === '') continue;
    params.append(name, value);
  }
  return params.toString();
}

// The To field a price slider position asks for: its top is no upper limit, so the field is emptied there.
export function priceField(value, max) {
  const n = Number(value);
  return Number.isFinite(n) && n < Number(max) ? String(n) : '';
}

// The search tab a page shows: the Setups panel while #setups is the target, else the tab the server marked.
export const tabFor = (hash, fallback) => (hash === '#setups' ? 'setups' : fallback);

// Where the search bar's Back goes: back in history when the shopper came from this store, else the link (the home page).
export function backTarget(referrer, origin) {
  try {
    return referrer && new URL(referrer).origin === origin ? 'history' : 'link';
  } catch {
    return 'link';
  }
}

// The same URL asking Shopify for one section's HTML only.
export function sectionUrl(url, sectionId, origin = 'https://shop.invalid') {
  const target = new URL(url, origin);
  target.searchParams.set('section_id', sectionId);
  return url.startsWith('http') ? target.toString() : `${target.pathname}${target.search}`;
}

// Predictive search query suggestions worth offering: different from the terms, no duplicates, at most four.
export function suggestions(json, terms) {
  const seen = new Set([String(terms ?? '').trim().toLowerCase()]);
  const out = [];
  for (const query of json?.resources?.results?.queries ?? []) {
    const text = String(query?.text ?? '').trim();
    if (!text || seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    out.push(text);
    if (out.length === 4) break;
  }
  return out;
}

// A click the script may take over: the main button with no modifier, so Cmd/Ctrl/Shift-click still opens a new tab or window.
export const isPlainClick = event => (event.button ?? 0) === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

// Two URLs show the same listing when only their hash differs (the skip link, an anchor).
export function sameDocument(a, b, origin = 'https://shop.invalid') {
  const one = new URL(a, origin);
  const two = new URL(b, origin);
  return one.pathname === two.pathname && one.search === two.search;
}

// A fresh section can replace the page in place only when it has exactly the regions the page has; otherwise navigate.
export function swapPlan(current, fresh) {
  const missing = current.filter(key => !fresh.includes(key));
  const extra = fresh.filter(key => !current.includes(key));
  return { ok: missing.length === 0 && extra.length === 0, missing };
}

// Numbers each render, so a response that arrives after a newer request started is dropped.
export function sequencer() {
  let n = 0;
  return { next: () => (n += 1), current: id => id === n, value: () => n };
}

// Where Tab goes inside the open filter sheet: wrap from the last control to the first (and back with Shift); null keeps the default.
export function trapIndex(index, count, backwards) {
  if (count === 0) return null;
  if (index < 0) return backwards ? count - 1 : 0;
  if (!backwards && index === count - 1) return 0;
  if (backwards && index === 0) return count - 1;
  return null;
}

// The grid density a stored value asks for; anything else is the default.
export const densityOf = value => (value === 'compact' ? 'compact' : 'comfortable');

// The number a search count section answers with, or null when the answer is not one.
export function countFrom(html) {
  const match = /data-ns-search-count[^>]*>\s*(\d+)\s*</.exec(String(html ?? ''));
  return match ? Number(match[1]) : null;
}

// The URL that asks Shopify how many results one search tab has: the terms, the type (none for All) and the count section.
export function countUrl(root, terms, type) {
  const params = new URLSearchParams({ q: terms, 'options[prefix]': 'last' });
  if (type && type !== 'all') params.set('type', type);
  params.set('section_id', 'ns-search-count');
  return `${String(root || '/').replace(/\/?$/, '/')}search?${params.toString()}`;
}

// A fetched tab count plus what the page adds to it: the Help answers (07 Interactive Prototype SEARCH), which Shopify's
// search does not count, ride on the tab as data-ns-tab-extra.
export function tabTotal(count, extra) {
  const add = Number.parseInt(extra, 10);
  return count + (Number.isFinite(add) ? add : 0);
}

if (typeof document !== 'undefined' && !window.nsShop) {
  window.nsShop = true;
  const phone = window.matchMedia('(max-width: 749px)');
  const tablet = window.matchMedia('(max-width: 1099px)');
  const root = node => node?.closest?.('[data-ns-shop]');
  // A panel is a sheet while the stylesheet fixes it to the screen; only then does it trap focus.
  const isSheet = panel => window.getComputedStyle(panel).position === 'fixed';
  const visible = node => node.getClientRects().length > 0;
  const openers = id => [...document.querySelectorAll(`[data-ns-facets-open][aria-controls="${CSS.escape(id)}"]`)];
  let lastOpener = null;
  // Search tabs the page could not count ask Shopify for their own count, once per tab; a filter change re-renders the
  // tabs without counts, so they are filled again from what was asked before.
  const tabCounts = new Map();
  const fillCounts = () => {
    const tabs = document.querySelector('[data-ns-search-tabs]');
    if (!tabs) return;
    const terms = tabs.getAttribute('data-ns-search-tabs');
    const base = window.Shopify?.routes?.root || '/';
    for (const slot of tabs.querySelectorAll('[data-ns-tab-count]')) {
      if (slot.textContent.trim() !== '') continue;
      const type = slot.getAttribute('data-ns-tab-count');
      if (!tabCounts.has(type)) {
        tabCounts.set(type, fetch(countUrl(base, terms, type)).then(response => (response.ok ? response.text() : '')).then(countFrom).catch(() => null));
      }
      tabCounts.get(type).then(count => { if (count !== null && slot.isConnected) slot.textContent = ` · ${tabTotal(count, slot.getAttribute('data-ns-tab-extra'))}`; });
    }
  };
  const DENSITY = 'ns:density';
  const readDensity = () => { try { return densityOf(window.localStorage.getItem(DENSITY)); } catch { return 'comfortable'; } };
  const syncDensity = shop => {
    const value = shop.getAttribute('data-density') || 'comfortable';
    for (const button of shop.querySelectorAll('[data-ns-density]')) button.setAttribute('aria-pressed', String(button.getAttribute('data-ns-density') === value));
  };
  const regions = scope => [...scope.querySelectorAll('[data-ns-swap]')].map(region => region.getAttribute('data-ns-swap'));
  const focusables = panel => [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"])')].filter(node => node.offsetParent !== null);
  const seq = sequencer();
  let controller;
  let rendering = false;
  let lastUrl = window.location.href;

  // Replace each region from the fresh section. Keep the open sheet open where it was scrolled, put focus back on the
  // control by id (else on the result count), and announce the new count in the live region that is never swapped.
  const swap = (shop, next) => {
    const active = document.activeElement;
    const focused = active?.id;
    const hadFocus = shop.contains(active) || active === document.body;
    const panel = shop.querySelector('[data-ns-facets-panel][data-open]');
    const open = panel?.id;
    const scroll = panel?.scrollTop ?? 0;
    for (const region of shop.querySelectorAll('[data-ns-swap]')) {
      region.innerHTML = next.querySelector(`[data-ns-swap="${region.getAttribute('data-ns-swap')}"]`).innerHTML;
    }
    if (open) {
      const fresh = document.getElementById(open);
      fresh?.setAttribute('data-open', '');
      if (fresh) fresh.scrollTop = scroll;
      for (const opener of openers(open)) opener.setAttribute('aria-expanded', 'true');
    }
    syncDensity(shop);
    fillCounts();
    const status = shop.querySelector('[data-ns-facets-status]');
    const live = shop.querySelector('[data-ns-facets-live]');
    if (live && status) live.textContent = status.textContent.trim();
    if (!hadFocus) return;
    const target = (focused && document.getElementById(focused)) || (open ? document.getElementById(open)?.querySelector('input, button') : status);
    target?.focus({ preventScroll: true });
  };

  const render = async (shop, url, push = true) => {
    const id = seq.next();
    controller?.abort();
    controller = new AbortController();
    rendering = true;
    const results = shop.querySelector('[data-ns-swap="results"]');
    results?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(sectionUrl(url, shop.getAttribute('data-ns-shop')), { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      if (!seq.current(id)) return;
      const next = new DOMParser().parseFromString(html, 'text/html');
      if (!swapPlan(regions(shop), regions(next)).ok) {
        window.location.assign(url);
        return;
      }
      swap(shop, next);
      if (push) history.pushState({ nsShop: true }, '', url);
      lastUrl = window.location.href;
    } catch (error) {
      if (error.name !== 'AbortError' && seq.current(id)) window.location.assign(url);
    } finally {
      if (seq.current(id)) {
        rendering = false;
        results?.removeAttribute('aria-busy');
      }
    }
  };

  const formUrl = form => {
    const query = facetQuery(new FormData(form).entries());
    return `${form.getAttribute('action')}${query ? `?${query}` : ''}`;
  };

  // The price slider fills the To field as it moves; its change then filters like any other field.
  document.addEventListener('input', event => {
    const range = event.target.closest?.('[data-ns-price-range]');
    const field = range && document.getElementById(range.getAttribute('data-ns-price-range'));
    if (field) field.value = priceField(range.value, range.max);
  });

  let timer;
  document.addEventListener('change', event => {
    const form = event.target.form;
    if (!form?.matches?.('[data-ns-facets]')) return;
    const shop = root(form) ?? root(event.target);
    if (!shop) return;
    // A sort picked in the sort sheet closes it, so the fresh listing is what the shopper sees next.
    event.target.closest?.('.ns-sort-sheet[data-open]')?.querySelector('[data-ns-facets-close]')?.click();
    clearTimeout(timer);
    const delay = event.target.type === 'number' ? 500 : 0;
    timer = setTimeout(() => render(shop, formUrl(form)), delay);
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches?.('[data-ns-facets]')) return;
    const shop = root(form);
    if (!shop) return;
    event.preventDefault();
    clearTimeout(timer);
    render(shop, formUrl(form));
    shop.querySelector('[data-ns-facets-panel][data-open] [data-ns-facets-close]')?.click();
  });

  // A popover (the search page's sort list from 750 px) closes without moving focus: the click that closed it goes on.
  const dismiss = panel => {
    panel.removeAttribute('data-open');
    for (const opener of openers(panel.id)) opener.setAttribute('aria-expanded', 'false');
  };
  const dismissPopover = (panel, target) => {
    if (!panel || panel.contains(target) || target.closest?.('[data-ns-facets-open]')) return;
    if (!isSheet(panel)) dismiss(panel);
  };

  const closePanel = panel => {
    panel.removeAttribute('data-open');
    document.documentElement.classList.remove('ns-facets-lock');
    const all = openers(panel.id);
    for (const opener of all) opener.setAttribute('aria-expanded', 'false');
    const back = lastOpener?.isConnected && visible(lastOpener) ? lastOpener : all.find(visible);
    lastOpener = null;
    back?.focus();
  };

  document.addEventListener('click', async event => {
    const back = event.target.closest?.('a[data-ns-back]');
    if (back && isPlainClick(event) && backTarget(document.referrer, window.location.origin) === 'history') {
      event.preventDefault();
      window.history.back();
      return;
    }
    // A click on the dimmed page around an open sheet closes it and goes no further.
    const sheet = document.querySelector('[data-ns-facets-panel][data-open]');
    if (sheet && !sheet.contains(event.target) && isSheet(sheet)) {
      event.preventDefault();
      closePanel(sheet);
      return;
    }
    dismissPopover(sheet, event.target);
    const link = event.target.closest?.('a[data-ns-facet-link]');
    if (link) {
      const shop = root(link);
      if (!shop || !isPlainClick(event)) return;
      event.preventDefault();
      render(shop, link.href);
      return;
    }
    const more = event.target.closest?.('a[data-ns-load-more]');
    if (more) {
      const shop = root(more);
      const grid = shop?.querySelector('[data-ns-results]');
      if (!grid || !isPlainClick(event)) return;
      event.preventDefault();
      if (rendering || more.getAttribute('aria-busy') === 'true') return;
      const id = seq.value();
      more.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(sectionUrl(more.href, shop.getAttribute('data-ns-shop')));
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        if (id !== seq.value() || !grid.isConnected) return;
        const next = new DOMParser().parseFromString(html, 'text/html');
        let first = null;
        for (const list of next.querySelectorAll('[data-ns-results]')) {
          const target = shop.querySelector(`[data-ns-results="${CSS.escape(list.getAttribute('data-ns-results'))}"]`);
          const items = [...list.children];
          if (!target || items.length === 0) continue;
          first ??= items[0];
          target.append(...items);
        }
        const fresh = next.querySelector('[data-ns-swap="more"]');
        const region = shop.querySelector('[data-ns-swap="more"]');
        if (region) region.innerHTML = fresh ? fresh.innerHTML : '';
        first?.querySelector('a, button')?.focus({ preventScroll: true });
      } catch {
        if (id === seq.value()) window.location.assign(more.href);
      } finally {
        more.removeAttribute('aria-busy');
      }
      return;
    }
    const open = event.target.closest?.('[data-ns-facets-open]');
    if (open) {
      const panel = document.getElementById(open.getAttribute('aria-controls'));
      if (!panel) return;
      if (panel.hasAttribute('data-open') && !isSheet(panel)) {
        dismiss(panel);
        return;
      }
      panel.setAttribute('data-open', '');
      for (const opener of openers(panel.id)) opener.setAttribute('aria-expanded', 'true');
      lastOpener = open;
      if (isSheet(panel)) document.documentElement.classList.add('ns-facets-lock');
      // A filter pill opens the sheet at its own group.
      const group = document.getElementById(open.getAttribute('data-ns-facets-focus') || '');
      if (group && panel.contains(group)) {
        group.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        group.querySelector('input:not([disabled])')?.focus({ preventScroll: true });
      } else {
        (panel.querySelector('input:checked') || panel.querySelector('input, button, a'))?.focus();
      }
      return;
    }
    const close = event.target.closest?.('[data-ns-facets-close]');
    if (close) {
      const panel = close.closest('[data-ns-facets-panel]');
      if (panel) closePanel(panel);
      return;
    }
    const density = event.target.closest?.('[data-ns-density]');
    if (density) {
      const shop = root(density);
      if (!shop) return;
      const value = densityOf(density.getAttribute('data-ns-density'));
      shop.setAttribute('data-density', value);
      syncDensity(shop);
      try { window.localStorage.setItem(DENSITY, value); } catch {}
    }
  });

  document.addEventListener('keydown', event => {
    const panel = document.querySelector('[data-ns-facets-panel][data-open]');
    if (!panel) return;
    if (event.key === 'Escape') {
      panel.querySelector('[data-ns-facets-close]')?.click();
      return;
    }
    if (event.key !== 'Tab' || !isSheet(panel)) return;
    const items = focusables(panel);
    const index = trapIndex(items.indexOf(document.activeElement), items.length, event.shiftKey);
    if (index === null) return;
    event.preventDefault();
    items[index].focus();
  });

  // Crossing a breakpoint can turn an open sheet back into the sidebar or the sort pills: close it then.
  const settle = () => {
    const panel = document.querySelector('[data-ns-facets-panel][data-open]');
    if (panel && !isSheet(panel)) closePanel(panel);
  };
  phone.addEventListener?.('change', settle);
  tablet.addEventListener?.('change', settle);

  window.addEventListener('popstate', () => {
    if (sameDocument(lastUrl, window.location.href)) return;
    lastUrl = window.location.href;
    const shop = document.querySelector('[data-ns-shop]');
    if (shop?.querySelector('[data-ns-facets]')) render(shop, window.location.href, false);
  });

  for (const shop of document.querySelectorAll('[data-ns-shop]')) {
    shop.setAttribute('data-js', '');
    if (shop.querySelector('[data-ns-density]')) {
      shop.setAttribute('data-density', readDensity());
      syncDensity(shop);
    }
  }

  fillCounts();

  // The Setups tab is an anchor to its panel (CSS :target swaps it for the listing); aria-current follows the hash.
  const serverTab = document.querySelector('[data-ns-search-tabs] [aria-current="page"]');
  const markTab = () => {
    const setupsTab = document.querySelector('[data-ns-setups-tab]');
    if (!setupsTab) return;
    const onSetups = tabFor(window.location.hash, 'other') === 'setups';
    if (onSetups) setupsTab.setAttribute('aria-current', 'page');
    else setupsTab.removeAttribute('aria-current');
    if (serverTab?.isConnected) {
      if (onSetups) serverTab.removeAttribute('aria-current');
      else serverTab.setAttribute('aria-current', 'page');
    }
  };
  window.addEventListener('hashchange', markTab);
  markTab();

  const box = document.querySelector('[data-ns-did-you-mean]');
  if (box) {
    const terms = box.getAttribute('data-ns-did-you-mean');
    const base = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    fetch(`${base}search/suggest.json?q=${encodeURIComponent(terms)}&resources[type]=query&resources[limit]=6`)
      .then(response => (response.ok ? response.json() : null))
      .then(json => {
        const list = box.querySelector('[data-ns-did-you-mean-list]');
        const words = suggestions(json, terms);
        if (!list || words.length === 0) return;
        // Shopify's own suggestions replace the menu's popular searches the section rendered.
        list.replaceChildren();
        for (const word of words) {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'ns-search-page__suggestion';
          link.href = `${base}search?q=${encodeURIComponent(word)}&options%5Bprefix%5D=last`;
          link.textContent = word;
          item.append(link);
          list.append(item);
        }
        box.hidden = false;
      })
      .catch(() => {});
  }
}
